import _ from 'lodash';
import jwtDecode from 'jwt-decode';

import { LOGIN_ENDPOINT } from '../utils/constants';

import * as models from '../utils/models';
import * as utils from '../utils/utilities';
import * as roleService from './roleService';
import * as userService from './userService';

import cookieService from './cookieService';
import httpService from './httpService';

interface AuthPayload { 
	email: string; 
	password: string;
	remember: boolean; 
}

export function getToken() {
	const session = cookieService.retrieveUserSession();
	return session ? session.token : null;
}

export function initializeAuthHeader() {
	let token = getToken();
	if (token) httpService.setToken(token);
}

export async function loginWithCredentials(data: AuthPayload, onUploadProgress: models.ProgressChange) {
	const credentials = _.omit(data, ['remember']);

	const result = await httpService.post(LOGIN_ENDPOINT, credentials, { onUploadProgress });
	const token: string = result.data.token;
	httpService.setToken(token);

	const user: models.User = result.data.retrievedUser;
	const roleResult = await roleService.getRole(user.roleId as string);
	user.role = utils.transformRoleItem(roleResult.data);

	cookieService.storeUserSession({ user, token });
}

export async function loginWithMicrosoftToken(token: string) {
	const decodedToken: { unique_name: string } | null = jwtDecode(token);
	if (!decodedToken) throw new Error('Invalid token!');

	const result = await httpService.post(LOGIN_ENDPOINT + '/microsoft', { token });
	const authToken: string = result.data.token;
	httpService.setToken(authToken);

	const user: models.User = result.data.retrievedUser;

	const roleResult = await roleService.getRole(user.roleId as string);
	user.role = utils.transformRoleItem(roleResult.data);

	cookieService.storeUserSession({ user, token: authToken });
}

export async function refreshUserSession() {
	const response = await userService.getMyProfile();

	const session = cookieService.retrieveUserSession();
	const roleResult = await roleService.getRole(response.data.roleId);

	const user = _.merge(response.data, { role: utils.transformRoleItem(roleResult.data) });
	
	cookieService.storeUserSession({ 
		token: session.token,
		user, 
	});
}

export function getCurrentUser(): models.User | null {
	const session = cookieService.retrieveUserSession();
	if (session) return session.user;

	return null;
}

export function decodeToken(token: string): models.Role | null {
	try {
		return jwtDecode(token);
	} catch (error) {
		return null;
	}
}

export function logout(): void {
	cookieService.removeUserSession();
}

const authService = {
	loginWithCredentials,
	loginWithMicrosoftToken,
	logout,
	getCurrentUser,
	getToken,
	refreshUserSession,
};

export default authService;
